import { useEffect, useId } from "react";
import { useAgentStore, type AssistantContextItem } from "@/stores/agent-store";

export type { AssistantContextItem };

// Register live "what the user currently sees" facts for the in-app assistant (motive 1,
// agent-service ADR 0020). Mirrors CopilotKit's useCopilotReadable: the items are registered on
// mount, replaced when they change, and cleared on unmount — so the set of registered items always
// equals what is on screen right now (a page that unmounts can't leave stale context behind). The
// AssistantDrawer merges every registered item into the turn's uiContext, alongside the route.
//
// Pass null/[] while the page's data is still loading (or when there's nothing worth surfacing);
// nothing is registered until real values exist. Values must be short display strings — surface the
// user's OWN data freely, but for other people's records prefer counts + view-state over dumping PII.
export function useAssistantContext(items: AssistantContextItem[] | null | undefined): void {
  const id = useId();
  const setUiContext = useAgentStore((s) => s.setUiContext);
  const clearUiContext = useAgentStore((s) => s.clearUiContext);

  // `items` is a fresh array each render; serialize it to a stable string so the effect only re-runs
  // (and only writes to the store) when the content actually changes. `key` IS a faithful hash of
  // `items`, so the effect re-parses it rather than depending on the array itself.
  const key = items && items.length ? JSON.stringify(items) : "";

  useEffect(() => {
    if (key) setUiContext(id, JSON.parse(key) as AssistantContextItem[]);
    else clearUiContext(id);
    return () => clearUiContext(id);
  }, [id, key, setUiContext, clearUiContext]);
}
